/**
 * Der Abschnitt „Referenzen" (`ship-referenzen`) - urspruenglich Tracking-/Container-Nummer UND
 * Speditionsportal, WOERTLICH umgezogen aus `MainTab/Stammdaten.tsx` (`index.html:8437-8446`) in
 * den neuen Reiter „Spedition" (Auftrag „Der neue Drawer-Reiter ‚Spedition' und der Feldumzug",
 * 2026-08-20, Abschnitt 2 „ship-referenzen"). Beschriftung, Platzhalter, Klassen und Kommentare
 * damals unveraendert uebernommen - siehe Kopfkommentar von `Stammdaten.tsx` zum Herkunftsort. Die
 * Tracking-/Container-Nummer ist seither WIEDER RAUS (siehe „Tracking-Nr." unten) - der Abschnitt
 * fuehrt seit der Korrektur „Trackingabschnitt ersatzlos entfernen" (2026-08-25) nur noch das
 * Speditionsportal.
 *
 * ================= Portal-Link: Eingabefeld weg, Ableitung aus der Sendung (Auftrag „Sendungs-
 * Dropdown, Portal-Link, Konfliktdialog", 2026-08-21, Ziel 1) =================
 *
 * Das Eingabefeld „Link zum Speditionsportal (Tracking) einfuegen…" war ein ZOMBIE: `forwarderUrl`
 * steht in KEINER Feldgruppe (`api/bestellungen.ts#KEIN_FELDGRUPPEN_WEG`), `bestellungAendern`
 * weist es mit `FeldOhneGruppeFehler` ab - der Tastendruck ging ins Leere. Die tote Verdrahtung in
 * `api/bestellungen.ts` und der `Order`-Typ bleiben UNANGETASTET stehen (sie belegt weiterhin die
 * Feldgruppen-Regel, `test/reference/bundle.test.ts` zaehlt sie mit) - entfernt ist NUR dieses
 * Eingabefeld, die Anzeige zieht auf `sendung.portalUrl` um.
 *
 * `sendung` ist OPTIONAL und seit der Nachbesserung B2 verdrahtet: `SpeditionTab.tsx` reicht sie
 * durch (`sendung={sendung}`), derselbe Weg wie `DsvSendung.tsx`s `bestellungId` (siehe dessen
 * Kopfkommentar). Fehlt die Prop dennoch, bleibt der Knopf inaktiv, mit dem Hinweistext darunter -
 * die sichere Seite (kein Link ohne Beleg ist besser als ein falscher).
 *
 * ================= Tracking-Nr.: der ganze Abschnitt ist WEG, ersatzlos (Auftrag „Trackingabschnitt
 * ersatzlos entfernen (Korrektur zu Auftrag 1)", 2026-08-25) =================
 *
 * Das Eingabefeld „Tracking- / Container-Nr." war GEMESSEN ohne jede Wirkung: kein Schreibweg
 * (`trackingNo` steht seit dem 2026-08-15 in `KEIN_FELDGRUPPEN_WEG`, `App.tsx` reicht `onTracking`
 * an `<SpeditionTab>` gar nicht durch) UND kein Leseweg (die Spalte `tracking_no` existiert im
 * neuen Schema nicht mehr, `backend/migrations/001_fundament.sql:75`; `o.trackingNo` war dadurch
 * dauerhaft `undefined`). Fachlich abgeloest: `feldinventar.ts:114` - die Nummer LEBT als
 * `shipments.dsv_shipment_no` + `order_shipments`, also an der verknuepften Sendung, nicht mehr an
 * der Bestellung.
 *
 * Der Auftrag „Trackingfeld ablösen statt Eingabe vortäuschen" (2026-08-25, erste Runde desselben
 * Tages) hatte daraus zunaechst dieselbe Behandlung wie beim Portal-Link oben gemacht: das
 * Eingabefeld weg, an seine Stelle eine reine Anzeige aus `sendung?.dsvShipmentNo` (mit demselben
 * Hinweistext `tracking-lock-note`, wenn keine Sendung haengt). Die anschliessende Pruefung hat
 * das als REDUNDANT verworfen: `Drawer/ShipTab/DsvSendung.tsx` zeichnet im SELBEN Reiter, WEITER
 * OBEN (Abschnitt „DSV-Sendung"), bereits `<Anzeigefeld label="DSV-Sendungsnr."
 * wert={sendung.dsvShipmentNo} />` - dieselbe Nummer - UND `<Anzeigefeld label="Container-Nr."
 * wert={sendung.containerNo} />` - die tatsaechliche Container-Nummer. Dieser Abschnitt zeigte die
 * Sendungsnummer also ein zweites Mal und versprach im Label eine Container-Nummer, die zwei
 * Zeilen hoeher unter eigenem, zutreffendem Namen bereits stand - „wenn es abgeloest ist, gehoert
 * es raus statt eine Eingabe vorzutaeuschen" (Auftraggeber). Der Abschnitt „Speditionsportal" oben
 * ist von dieser zweiten Korrektur NICHT betroffen - er hat kein Gegenstueck in `DsvSendung.tsx`.
 *
 * Die Prop `onTracking` bleibt deshalb WEITERHIN weg (aus `ReferenzenProps`, `SpeditionTabProps`
 * und der Durchreichung in `SpeditionTab.tsx`) - das galt schon seit der ersten Runde, siehe oben.
 * `trackingNo` selbst bleibt als BENANNTE LUECKE in `api/bestellungen.ts#KEIN_FELDGRUPPEN_WEG`
 * stehen (derselbe Grund gilt weiter: die Spalte gibt es nicht), dieser Abschnitt liest
 * `o.trackingNo` seither gar nicht mehr - und zeigt `sendung.dsvShipmentNo` seither auch nicht mehr
 * an.
 */
import type { Order, SendungsTreffer } from "../../../lib/types.ts";

/**
 * `icon(name, className)` (`index.html:4016`) samt der Nachbearbeitung durch `hydrateIcons`
 * (`:4021`) - die gemeinsame Registratur zeichnet das fertige `<svg class="lucide lucide-…">`.
 */
import { Ikone } from "../../Ikone.tsx";

export interface ReferenzenProps {
  bestellung: Order;
  /**
   * Bleibt Pflichtprop fuer `SpeditionTab.tsx` (unveraendert, ausserhalb der Dateiliste dieses
   * Auftrags) - INNERHALB dieser Komponente seit Ziel 1 ohne Aufrufer: der einzige Schreibweg war
   * `patch("forwarderUrl", …)` fuer das jetzt entfernte Eingabefeld (siehe Kopfkommentar).
   */
  onAendern: (id: string, aenderung: Record<string, unknown>) => void;
  /**
   * `openPortal()` (index.html:9316). Der Bestand liest die offene Bestellung aus dem Global
   * `openId`; hier reicht die Komponente die id mit.
   */
  onPortal?: (id: string) => void;
  /**
   * NEU (Ziel 1) - Quelle von `sendung.portalUrl`. Bis zur Korrektur „Trackingabschnitt ersatzlos
   * entfernen" (2026-08-25) war sie hier auch Quelle von `sendung.dsvShipmentNo` fuer den
   * inzwischen entfernten Tracking-Abschnitt - die Nummer steht seither ausschliesslich in
   * `DsvSendung.tsx` (Label „DSV-Sendungsnr."). Siehe Kopfkommentar „Portal-Link" bzw.
   * „Tracking-Nr.".
   */
  sendung?: SendungsTreffer | null;
}

export function Referenzen({ bestellung: o, onAendern, onPortal, sendung }: ReferenzenProps) {
  const id = o.id as string;
  const portalUrl = sendung?.portalUrl ?? null;

  return (
    <div className="field wide">
      <label>Speditionsportal</label>
      {/*
       * Ziel 1: kein Eingabefeld mehr - der Link ergibt sich aus der verknuepften Sendung
       * (`sendung.portalUrl`), siehe Kopfkommentar. `forwarderUrl` bleibt als BENANNTE LUECKE in
       * `api/bestellungen.ts#KEIN_FELDGRUPPEN_WEG` stehen (`shipments.portal_url` wird
       * ausschliesslich vom DSV-Abgleich geschrieben, nie von Hand) - dieser Abschnitt liest das
       * Feld nicht mehr, patcht es also auch nicht mehr.
       */}
      <div className="portal-row">
        <button
          className="portal-open"
          type="button"
          disabled={!portalUrl}
          onClick={() => onPortal?.(id)}
        >
          <Ikone name="external-link" />
          Im DSV-Portal öffnen
        </button>
        {!portalUrl && (
          <span className="tracking-lock-note">
            erscheint automatisch, sobald eine Sendung verknüpft ist
          </span>
        )}
      </div>
    </div>
  );
}
